import {
  find,
  findIndex,
  get,
  has,
  isEmpty,
  isNil,
  last,
  omit,
  pick,
  upperFirst,
} from "lodash";
import moment from "moment";
import "moment/locale/fr";
import { getSourceMode } from "shared/components/SourceEditor";
import { getChildLoopIndex, hasSource, isStaticMapping } from "../../anchors";
import {
  CONDITION_OPERATORS,
  MAPPING_TYPES,
  RULE_OPERATORS,
  SOURCE_MODES,
} from "../../constants";
import {
  DATE_TRANSFORM_OP,
  DEFAULT_DATE_FORMAT,
  FIELD_GROUPS,
  FIELD_KINDS,
  fieldMapping,
  fieldOfZone,
  fieldPhrase,
  findFieldByPath,
  findFieldForMapping,
  groupLabel,
  readFieldValue,
  zoneBaseName,
} from "../../data-fields";
import { isZoneSettled, ZONE_TYPES, zonesOf } from "../../field-status";
import { PROPOSAL_STATUSES } from "./draft-content";
import { prefixGroup, zoneLabelParts } from "./zone-labels";
import { searchable } from "utils/helpers/text";

export const SECTIONS = {
  ...FIELD_GROUPS,
  SIGNATURE: "SIGNATURE",
  OTHER: "OTHER",
};

export const SUMMARY_KINDS = {
  FIELD: "FIELD",
  EMPTY: "EMPTY",
  FIXED: "FIXED",
  COMBINED: "COMBINED",
  ADVANCED: "ADVANCED",
  NONE: "NONE",
  SIGNATURE: "SIGNATURE",
  CHECKBOXES: "CHECKBOXES",
  TABLE: "TABLE",
};

export const ZONE_STATUSES = {
  READY: "ready",
  CHECK: "check",
  TODO: "todo",
  EMPTY: "empty",
  SIGN: "sign",
};

export const isPendingStatus = (status) =>
  status === ZONE_STATUSES.CHECK || status === ZONE_STATUSES.TODO;

export const LEAVE_EMPTY_MAPPING = {
  type: MAPPING_TYPES.STATIC,
  staticValue: "",
};

const SOURCE_KEYS = ["key", "keys", "from", "combine", "static", "staticValue"];

const isBlank = (value) => isNil(value) || value === "";

const asObject = (value) =>
  !isNil(value) && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

export const withoutKey = (object, key) =>
  Object.fromEntries(
    Object.entries(object || {}).filter(([entry]) => entry !== key),
  );

export const fieldTitle = (field) =>
  `${groupLabel(field.group)} › ${field.label}`;

export const zoneMappingKeys = (zone) => {
  if (zone.type === ZONE_TYPES.SIGNATURE) return [];
  if (zone.type === ZONE_TYPES.CHECKBOX_GROUP) return zone.anchors;
  return [zone.key];
};

export const zoneName = (zone) => zoneBaseName(zone.id);

export const zoneDictionaryLabel = (zone) => {
  if (zone.type !== ZONE_TYPES.ANCHOR && zone.type !== ZONE_TYPES.SIGNATURE)
    return undefined;
  const field = fieldOfZone(zone.id);
  return isNil(field) ? undefined : fieldPhrase(field);
};

export const findZone = (anchors, id) =>
  zonesOf(anchors).find((zone) => zone.id === id);

export const checkboxKey = (zone, mappings = {}) =>
  zone.anchors
    .map((anchor) => get(mappings[anchor], "conditions.rules.0.key"))
    .find((key) => !isEmpty(key));

export const listPathOf = (mapping) =>
  get(mapping, "from.path") ?? get(mapping, "key");

export const zoneField = (zone, mappings = {}) => {
  if (zone.type === ZONE_TYPES.CHECKBOX_GROUP)
    return findFieldByPath(checkboxKey(zone, mappings));
  if (zone.type === ZONE_TYPES.CHILD_LOOP)
    return findFieldByPath(listPathOf(mappings[zone.key]));
  if (zone.type === ZONE_TYPES.ANCHOR)
    return findFieldForMapping(asObject(mappings[zone.key]));
  return undefined;
};

export const zoneSection = (zone, mappings) => {
  if (zone.type === ZONE_TYPES.SIGNATURE) return SECTIONS.SIGNATURE;
  const field = zoneField(zone, mappings) ?? fieldOfZone(zone.id);
  return field?.group ?? prefixGroup(zone) ?? SECTIONS.OTHER;
};

export const mappingPaths = (mapping) => {
  if (typeof mapping === "string") return [mapping];
  const source = asObject(mapping);
  return [
    source.key,
    ...(source.keys || []),
    get(source, "from.path"),
    ...(get(source, "combine.parts") || []).flatMap(mappingPaths),
  ].filter((path) => !isEmpty(path));
};

export const zoneIsEmptyWith = (zone, mappings, unresolved) =>
  zoneMappingKeys(zone).some((key) =>
    mappingPaths(mappings[key]).some((path) => unresolved.has(path)),
  );

export const withSource = (mapping, source) => {
  const rest = omit(asObject(mapping), SOURCE_KEYS);
  return {
    ...(isStaticMapping(mapping) ? omit(rest, ["type"]) : rest),
    ...source,
  };
};

export const dateTransformOf = (mapping) =>
  find(get(mapping, "transforms") || [], { op: DATE_TRANSFORM_OP });

export const withDateFormat = (mapping, format) => {
  const transforms = get(mapping, "transforms") || [];
  if (isNil(format)) {
    const rest = transforms.filter(
      (transform) => transform.op !== DATE_TRANSFORM_OP,
    );
    return isEmpty(rest)
      ? omit(mapping, ["transforms"])
      : { ...mapping, transforms: rest };
  }
  const index = findIndex(transforms, { op: DATE_TRANSFORM_OP });
  if (index < 0)
    return {
      ...mapping,
      transforms: [{ op: DATE_TRANSFORM_OP, format }, ...transforms],
    };
  return {
    ...mapping,
    transforms: transforms.map((transform, position) =>
      position === index ? { ...transform, format } : transform,
    ),
  };
};

export const mappingForField = (zone, field, mapping) => {
  if (zone.type === ZONE_TYPES.CHILD_LOOP)
    return {
      ...withSource(mapping, { from: { path: field.path } }),
      type: MAPPING_TYPES.CHILD_LOOP,
    };
  const next = withSource(mapping, fieldMapping(field));
  if (field.kind !== FIELD_KINDS.DATE) return withDateFormat(next, null);
  return isNil(dateTransformOf(next))
    ? withDateFormat(next, DEFAULT_DATE_FORMAT)
    : next;
};

export const fieldFitsZone = (zone, field) => {
  if (isNil(field)) return false;
  if (zone.type === ZONE_TYPES.CHILD_LOOP)
    return field.kind === FIELD_KINDS.LIST;
  if (zone.type === ZONE_TYPES.ANCHOR) return field.kind !== FIELD_KINDS.LIST;
  return false;
};

export const pendingProposalOf = (zone, proposals = {}) => {
  const proposal = proposals[zone.id];
  if (get(proposal, "status") !== PROPOSAL_STATUSES.PENDING) return null;
  const field = findFieldByPath(proposal.path);
  return fieldFitsZone(zone, field) ? { ...proposal, field } : null;
};

const proposalWith = (proposal, status) => ({
  path: proposal.path,
  reason: proposal.reason ?? null,
  status,
});

export const acceptProposal = (content, zone) => {
  const proposal = pendingProposalOf(zone, content.proposals);
  if (isNil(proposal)) return content;
  return {
    ...content,
    anchorMappings: {
      ...content.anchorMappings,
      [zone.key]: mappingForField(
        zone,
        proposal.field,
        content.anchorMappings[zone.key],
      ),
    },
    proposals: {
      ...content.proposals,
      [zone.id]: proposalWith(proposal, PROPOSAL_STATUSES.ACCEPTED),
    },
  };
};

export const leaveZoneEmpty = (content, zone) => {
  const proposal = pendingProposalOf(zone, content.proposals);
  return {
    ...content,
    anchorMappings: {
      ...content.anchorMappings,
      [zone.key]: LEAVE_EMPTY_MAPPING,
    },
    proposals: isNil(proposal)
      ? content.proposals
      : {
          ...content.proposals,
          [zone.id]: proposalWith(proposal, PROPOSAL_STATUSES.DISMISSED),
        },
  };
};

export const commitZone = (content, zone, edited) => {
  const anchorMappings = zoneMappingKeys(zone).reduce(
    (mappings, key) =>
      isNil(edited[key])
        ? withoutKey(mappings, key)
        : { ...mappings, [key]: edited[key] },
    content.anchorMappings,
  );
  const proposal = pendingProposalOf(zone, content.proposals);
  if (isNil(proposal) || !isZoneSettled(zone, anchorMappings))
    return { ...content, anchorMappings };
  const status =
    get(zoneField(zone, anchorMappings), "path") === proposal.path
      ? PROPOSAL_STATUSES.ACCEPTED
      : PROPOSAL_STATUSES.DISMISSED;
  return {
    ...content,
    anchorMappings,
    proposals: {
      ...content.proposals,
      [zone.id]: proposalWith(proposal, status),
    },
  };
};

export const acceptedProposals = (content) =>
  zonesOf(content.anchors)
    .map((zone) => ({ zone, proposal: content.proposals[zone.id] }))
    .filter(
      ({ zone, proposal }) =>
        get(proposal, "status") === PROPOSAL_STATUSES.ACCEPTED &&
        get(zoneField(zone, content.anchorMappings), "path") === proposal.path,
    );

export const zoneSnapshot = (content, zones) => {
  const keys = zones.flatMap(zoneMappingKeys);
  const ids = zones.map((zone) => zone.id);
  return {
    mappings: pick(content.anchorMappings, keys),
    absentMappings: keys.filter((key) => !has(content.anchorMappings, key)),
    proposals: pick(content.proposals, ids),
    absentProposals: ids.filter((id) => !has(content.proposals, id)),
  };
};

export const restoreZones = (content, snapshot) => ({
  ...content,
  anchorMappings: omit(
    { ...content.anchorMappings, ...snapshot.mappings },
    snapshot.absentMappings,
  ),
  proposals: omit(
    { ...content.proposals, ...snapshot.proposals },
    snapshot.absentProposals,
  ),
});

export const undoProposal = (content, zone) => ({
  ...content,
  anchorMappings: withoutKey(content.anchorMappings, zone.key),
  proposals: {
    ...content.proposals,
    [zone.id]: proposalWith(
      content.proposals[zone.id],
      PROPOSAL_STATUSES.PENDING,
    ),
  },
});

export const zoneSummary = (zone, mappings) => {
  if (zone.type === ZONE_TYPES.SIGNATURE)
    return { kind: SUMMARY_KINDS.SIGNATURE };
  if (zone.type === ZONE_TYPES.CHECKBOX_GROUP)
    return {
      kind: SUMMARY_KINDS.CHECKBOXES,
      field: zoneField(zone, mappings),
      count: zone.anchors.length,
    };
  const mapping = mappings[zone.key];
  if (zone.type === ZONE_TYPES.CHILD_LOOP)
    return hasSource(asObject(mapping))
      ? {
          kind: SUMMARY_KINDS.TABLE,
          field: zoneField(zone, mappings),
          path: listPathOf(mapping),
        }
      : { kind: SUMMARY_KINDS.NONE };
  if (isStaticMapping(mapping))
    return isEmpty(mapping.staticValue)
      ? { kind: SUMMARY_KINDS.EMPTY }
      : { kind: SUMMARY_KINDS.FIXED, text: mapping.staticValue };
  if (!hasSource(asObject(mapping))) return { kind: SUMMARY_KINDS.NONE };
  const field = findFieldForMapping(mapping);
  if (!isNil(field)) return { kind: SUMMARY_KINDS.FIELD, field };
  if (getSourceMode(mapping) === SOURCE_MODES.COMBINE)
    return { kind: SUMMARY_KINDS.COMBINED };
  return { kind: SUMMARY_KINDS.ADVANCED };
};

export const formatDate = (value, format) => {
  const date = moment(value, moment.ISO_8601);
  return date.isValid()
    ? date.locale("fr").format(format || DEFAULT_DATE_FORMAT)
    : value;
};

const titleCase = (text) =>
  text.toLowerCase().replace(/(^|[\s-])\S/g, (letter) => letter.toUpperCase());

const CASES = {
  upper: (text) => text.toUpperCase(),
  lower: (text) => text.toLowerCase(),
  capitalize: (text) => upperFirst(text.toLowerCase()),
  title: titleCase,
};

const TRANSFORMERS = {
  date: (value, { format }) =>
    isBlank(value) ? value : formatDate(value, format),
  case: (value, { mode }) =>
    isBlank(value) ? value : (CASES[mode] || CASES.capitalize)(String(value)),
  prefix: (value, { value: prefix }) =>
    isBlank(value) ? value : `${prefix ?? ""}${value}`,
  suffix: (value, { value: suffix }) =>
    isBlank(value) ? value : `${value}${suffix ?? ""}`,
  default: (value, { value: fallback }) => (isBlank(value) ? fallback : value),
  trim: (value) => (typeof value === "string" ? value.trim() : value),
};

export const applyTransforms = (value, transforms) =>
  (transforms || []).reduce((current, transform) => {
    const transformer = TRANSFORMERS[transform.op];
    return isNil(transformer) ? current : transformer(current, transform);
  }, value);

const readPath = (payload, path) =>
  isEmpty(path) ? undefined : get(payload, path.replace(/\[N\]/g, "[0]"));

const readFrom = (from, payload) => {
  if (!Array.isArray(get(payload, get(from, "path"))))
    return get(from, "default");
  return (
    readFieldValue({ path: from.path, source: { from } }, payload) ??
    from.default
  );
};

const SOURCE_READERS = {
  [SOURCE_MODES.KEY]: (mapping, payload) => readPath(payload, mapping.key),
  [SOURCE_MODES.KEYS]: (mapping, payload) =>
    (mapping.keys || [])
      .map((key) => readPath(payload, key))
      .find((value) => !isBlank(value)),
  [SOURCE_MODES.FROM]: (mapping, payload) => readFrom(mapping.from, payload),
  [SOURCE_MODES.STATIC]: (mapping) => mapping.static,
  [SOURCE_MODES.COMBINE]: (mapping, payload) =>
    (get(mapping, "combine.parts") || [])
      .map((part) => readMappingValue(part, payload))
      .filter((value) => !isBlank(value))
      .join(get(mapping, "combine.separator") ?? " "),
};

export const readSourceValue = (mapping, payload) => {
  if (isNil(mapping)) return undefined;
  if (typeof mapping === "string") return readPath(payload, mapping);
  if (isStaticMapping(mapping)) return mapping.staticValue;
  return SOURCE_READERS[getSourceMode(mapping)](mapping, payload);
};

export const readMappingValue = (mapping, payload) =>
  applyTransforms(
    readSourceValue(mapping, payload),
    get(asObject(mapping), "transforms"),
  );

export const valueText = (value, { kind, t }) => {
  if (isBlank(value)) return null;
  if (Array.isArray(value)) return t("VALUES.ITEMS", { count: value.length });
  if (typeof value === "boolean") return t(value ? "VALUES.YES" : "VALUES.NO");
  if (typeof value === "object") return null;
  if (kind === FIELD_KINDS.DATE)
    return String(formatDate(value, DEFAULT_DATE_FORMAT));
  return String(value);
};

const RULE_CHECKS = {
  [RULE_OPERATORS.EQUALS]: (actual, expected) => actual === expected,
  [RULE_OPERATORS.NOT_EQUALS]: (actual, expected) => actual !== expected,
  [RULE_OPERATORS.CONTAINS]: (actual, expected) => actual.includes(expected),
  [RULE_OPERATORS.NOT_CONTAINS]: (actual, expected) =>
    !actual.includes(expected),
};

export const conditionsPass = (conditions, payload) => {
  const rules = get(conditions, "rules") || [];
  if (isEmpty(rules)) return true;
  const results = rules.map((rule) => {
    const actual = get(payload, rule.key);
    const check = RULE_CHECKS[rule.op];
    return isNil(check)
      ? true
      : check(isNil(actual) ? "" : String(actual), rule.value || "");
  });
  return conditions.operator === CONDITION_OPERATORS.AND
    ? results.every(Boolean)
    : results.some(Boolean);
};

export const columnFromZoneName = (zone) =>
  zone.key.replace(/^[A-Za-z]+\.N\./, "");

const fieldText = (parts, t) => {
  if (parts.signature) return t("ZONES.SIGNATURE");
  if (!parts.unknown) return parts.field;
  return parts.checkbox
    ? t("ZONES.CHECKBOXES_UNKNOWN", { name: parts.words })
    : t("ZONES.UNKNOWN_FIELD", { words: parts.words });
};

export const zoneLabel = (zone, t, { section } = {}) => {
  const dictionary = zoneDictionaryLabel(zone);
  if (!isNil(dictionary)) return dictionary;
  const parts = zoneLabelParts(zone);
  const field = fieldText(parts, t);
  if (isNil(parts.qualifier)) return field;
  if (!isEmpty(section) && searchable(section).includes(parts.core))
    return field;
  return `${field} ${parts.qualifier}`;
};

export const zoneStatus = ({ zone, mappings, proposals, unresolved }) => {
  if (zone.type === ZONE_TYPES.SIGNATURE) return ZONE_STATUSES.SIGN;
  if (!isZoneSettled(zone, mappings))
    return isNil(pendingProposalOf(zone, proposals))
      ? ZONE_STATUSES.TODO
      : ZONE_STATUSES.CHECK;
  return zoneIsEmptyWith(zone, mappings, unresolved)
    ? ZONE_STATUSES.EMPTY
    : ZONE_STATUSES.READY;
};

const checkedBoxes = (zone, mappings, payload) =>
  zone.anchors.filter(
    (anchor) =>
      !isNil(mappings[anchor]) &&
      conditionsPass(mappings[anchor].conditions, payload),
  );

const ZONE_VALUES = {
  [ZONE_TYPES.SIGNATURE]: (_zone, _mappings, _payload, t) =>
    t("ZONES.VALUE.SIGNATURE"),
  [ZONE_TYPES.CHECKBOX_GROUP]: (zone, mappings, payload, t) => {
    const checked = checkedBoxes(zone, mappings, payload);
    return isEmpty(checked)
      ? t("ZONES.VALUE.NONE_CHECKED")
      : t("ZONES.CHECKED_OF", {
          count: checked.length,
          total: zone.anchors.length,
        });
  },
  [ZONE_TYPES.CHILD_LOOP]: (zone, mappings, payload, t) => {
    const list = readPath(payload, listPathOf(mappings[zone.key]));
    return Array.isArray(list)
      ? t("ZONES.VALUE.ROWS", { count: list.length })
      : null;
  },
  [ZONE_TYPES.ANCHOR]: (zone, mappings, payload, t) => {
    const mapping = mappings[zone.key];
    return valueText(readMappingValue(mapping, payload), {
      kind: findFieldForMapping(asObject(mapping))?.kind,
      t,
    });
  },
};

export const zoneValueText = ({ zone, mappings, payload, t }) =>
  isZoneSettled(zone, mappings)
    ? ZONE_VALUES[zone.type](zone, mappings, payload, t)
    : null;

const SUMMARY_TEXTS = {
  [SUMMARY_KINDS.FIELD]: ({ field }) => fieldPhrase(field),
  [SUMMARY_KINDS.FIXED]: ({ text }, t) => t("ZONES.SUMMARY.FIXED", { text }),
  [SUMMARY_KINDS.CHECKBOXES]: ({ field, count }, t) =>
    isNil(field)
      ? t("ZONES.SUMMARY.CHECKBOXES", { count })
      : t("ZONES.SUMMARY.CHECKBOXES_FIELD", {
          count,
          field: fieldPhrase(field),
        }),
  [SUMMARY_KINDS.TABLE]: ({ field, path }, t) =>
    t("ZONES.SUMMARY.TABLE", {
      list: isNil(field) ? path : fieldPhrase(field),
    }),
};

export const summaryText = (summary, t) => {
  const text = SUMMARY_TEXTS[summary.kind];
  return isNil(text) ? t(`ZONES.SUMMARY.${summary.kind}`) : text(summary, t);
};

const settledTip = ({ zone, mappings, t }) => {
  const summary = zoneSummary(zone, mappings);
  return summary.kind === SUMMARY_KINDS.EMPTY
    ? t("PREVIEW.BOX_LEFT_EMPTY")
    : summaryText(summary, t);
};

const TIPS = {
  [ZONE_STATUSES.CHECK]: ({ zone, proposals, t }) =>
    t("PREVIEW.BOX_PROPOSAL", {
      field: fieldPhrase(pendingProposalOf(zone, proposals).field),
    }),
  [ZONE_STATUSES.TODO]: ({ t }) => t("PREVIEW.BOX_TODO"),
  [ZONE_STATUSES.EMPTY]: ({ t }) => t("PREVIEW.BOX_EMPTY"),
  [ZONE_STATUSES.SIGN]: ({ t }) => t("ZONES.STATUSES.sign"),
  [ZONE_STATUSES.READY]: settledTip,
};

export const zoneTip = ({ zone, status, mappings, proposals, t }) =>
  TIPS[status]({ zone, mappings, proposals, t });

const SAMPLE_ROWS = 3;

const lastSegment = (path) => last((path || "").split(".")) || "";

export const tableColumnOf = (zone, field) => {
  const column = lastSegment(columnFromZoneName(zone)).toLowerCase();
  return (field?.items || []).find(
    ({ itemKey }) => lastSegment(itemKey).toLowerCase() === column,
  );
};

const tableRowsText = ({ zone, field, list, t }) => {
  const column = tableColumnOf(zone, field);
  if (isNil(column)) return t("ZONES.VALUE.ROWS", { count: list.length });
  const values = list
    .slice(0, SAMPLE_ROWS)
    .map(
      (row) =>
        valueText(get(row, column.itemKey), { kind: column.kind, t }) ?? "—",
    );
  const more = list.length > SAMPLE_ROWS ? ", …" : "";
  return t("ZONES.ROWS_SAMPLE", {
    count: list.length,
    values: `${values.join(", ")}${more}`,
  });
};

const tableCellText = ({ zone, field, list, index, t }) => {
  const column = tableColumnOf(zone, field);
  if (isNil(column) || !Array.isArray(list))
    return t("PREVIEW.GHOST_ROW", { row: index + 1 });
  const row = list[index];
  if (isNil(row)) return null;
  return valueText(get(row, column.itemKey), { kind: column.kind, t });
};

export const proposalValueText = ({ zone, proposal, payload, t, index }) => {
  if (isNil(proposal)) return null;
  const value = readFieldValue(proposal.field, payload);
  if (zone.type !== ZONE_TYPES.CHILD_LOOP)
    return valueText(value, { kind: proposal.field.kind, t });
  if (!isNil(index))
    return tableCellText({
      zone,
      field: proposal.field,
      list: value,
      index,
      t,
    });
  return Array.isArray(value) && !isEmpty(value)
    ? tableRowsText({ zone, field: proposal.field, list: value, t })
    : null;
};

export const boxRowIndex = (box) => getChildLoopIndex(box.anchor ?? "");
